import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
    PiArrowDownRightBold,
    PiArrowRightBold,
    PiArrowUpRightBold,
    PiBuildingsBold,
    PiClockBold,
    PiCurrencyDollarBold,
    PiHandshakeBold,
    PiMedalBold,
    PiPercentBold,
    PiReceiptBold,
    PiStackBold,
    PiTrendUpBold,
    PiTrophyBold,
    PiUsersBold,
    PiWarningBold,
} from 'react-icons/pi';
import { Badge, LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';

type SellerRow = {
    owner_name: string;
    owner_id?: number | null;
    avatar_url?: string | null;
    rank?: number;
    share_pct?: number | null;
    amount: number;
    deal_count: number;
    client_count?: number;
    ticket_avg: number | null;
    discount_avg_simple: number | null;
    entry_avg_pct: number | null;
    pmr_months?: number | null;
};

type DealGovRow = {
    id: number;
    deal_number?: string | null;
    title?: string | null;
    contact_name?: string | null;
    owner_name?: string | null;
    owner_avatar_url?: string | null;
    amount: number;
    finish_date?: string | null;
    discount_pct?: number;
    entry_pct?: number;
    entry_floor_pct?: number;
};

type CrossSellBucket = {
    clients: number;
    amount: number;
    clients_pct: number | null;
    amount_pct: number | null;
};

type FinanceData = {
    year: number;
    source_note?: string;
    revenue_sources?: {
        line_total: number;
        deal_amount: number;
        negotiated: number;
        delta: number;
        method: string;
    };
    kpis?: {
        ytd_amount: number;
        ytd_count: number;
        mtd_amount: number;
        mtd_count: number;
        ticket_avg: number | null;
        mom_pct: number | null;
        yoy_pct: number | null;
        distinct_clients?: number;
        discount_avg_simple?: number | null;
        mb_pct?: number | null;
        mb_coverage_pct?: number | null;
    };
    margin?: {
        mb_pct: number | null;
        mb_coverage_pct: number | null;
        cost_source?: string;
        formula?: string;
    };
    monthly?: { labels: string[]; amounts: number[]; counts: number[]; tickets?: Array<number | null> };
    seasonality?: {
        labels: string[];
        current: number[];
        previous: number[];
        previous_year: number;
        current_year: number;
    };
    sellers?: SellerRow[];
    governance?: {
        discount_ceiling_pct: number;
        high_discount_count: number;
        low_entry_count: number;
        total_deals: number;
        high_discount_deals: DealGovRow[];
        low_entry_deals: DealGovRow[];
    };
    entry_by_month?: {
        labels: string[];
        negotiated: number[];
        entry_amount: number[];
        entry_pct: Array<number | null>;
    };
    by_category?: Array<{
        category: string;
        amount: number;
        quantity: number;
        deal_count: number;
        mb_pct?: number | null;
    }>;
    by_category_contract?: Array<{
        category: string;
        qty_sale: number;
        qty_rental: number;
        amount: number;
        amount_sale: number;
        amount_rental: number;
    }>;
    top_products?: Array<{ product_name: string; quantity: number; amount: number; ticket_avg: number | null }>;
    by_uf?: Array<{ uf: string; amount: number; deal_count: number }>;
    by_contract_type?: Array<{ contract_type: string; amount: number; deal_count: number }>;
    pmr?: {
        avg_months: number | null;
        sale_months: number | null;
        rental_months: number | null;
        sample_size: number;
        formula?: string;
    };
    cross_sell?: {
        total_clients: number;
        single_category: number;
        multi_category: number;
        full_portfolio: number;
        multi_pct: number | null;
        single_pct: number | null;
        category_count_benchmark: number;
        buckets?: {
            single: CrossSellBucket;
            multi: CrossSellBucket;
            full: CrossSellBucket;
        };
        total_amount?: number;
        unassigned_amount?: number;
        delta_vs_negotiated?: number;
    };
    audit?: {
        revenue_delta_deal_vs_negotiated: number;
        mb_coverage_pct: number | null;
        pmr_sample_size: number;
        cross_sell_unassigned_amount: number;
        open_gaps: string[];
    };
};

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v ?? 0);

const brlCompact = (v: number) => {
    if (Math.abs(v) >= 1_000_000) {
        return `R$ ${(v / 1_000_000).toFixed(1)}M`;
    }
    if (Math.abs(v) >= 1_000) {
        return `R$ ${(v / 1_000).toFixed(0)}K`;
    }
    return brl(v);
};

const pct = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${v.toFixed(1)}%`);

function initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
}

function avatarHue(name: string): number {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
    return Math.abs(hash) % 360;
}

function RankBadge({ rank, compact = false }: { rank: number; compact?: boolean }) {
    const tone =
        rank === 1
            ? 'bg-amber-100 text-amber-900 ring-amber-300 dark:bg-amber-900/50 dark:text-amber-100 dark:ring-amber-700'
            : rank === 2
              ? 'bg-slate-200 text-slate-800 ring-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:ring-slate-500'
              : rank === 3
                ? 'bg-orange-100 text-orange-900 ring-orange-300 dark:bg-orange-900/50 dark:text-orange-100 dark:ring-orange-700'
                : 'bg-gray-100 text-gray-700 ring-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:ring-gray-600';

    if (compact) {
        return (
            <span
                className={cn(
                    'inline-flex size-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold tabular-nums ring-1',
                    tone,
                )}
            >
                {rank}
            </span>
        );
    }

    const Icon = rank === 1 ? PiTrophyBold : rank <= 3 ? PiMedalBold : null;

    return (
        <span
            className={cn(
                'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-[11px] font-bold tabular-nums ring-1',
                tone,
            )}
        >
            {Icon ? <Icon className="size-3.5" /> : null}
            {rank <= 3 ? `Top ${rank}` : `#${rank}`}
        </span>
    );
}

function SellerAvatar({
    name,
    avatarUrl,
    size = 'md',
}: {
    name: string;
    avatarUrl?: string | null;
    size?: 'sm' | 'md';
}) {
    const dim = size === 'sm' ? 'size-7' : 'size-9';
    const text = size === 'sm' ? 'text-[10px]' : 'text-xs';

    if (avatarUrl) {
        return (
            <img
                src={avatarUrl}
                alt={name}
                className={cn(dim, 'shrink-0 rounded-full object-cover ring-2 ring-white dark:ring-gray-800')}
            />
        );
    }

    const hue = avatarHue(name || '?');
    return (
        <span
            className={cn(
                dim,
                text,
                'inline-flex shrink-0 items-center justify-center rounded-full font-bold text-white ring-2 ring-white dark:ring-gray-800',
            )}
            style={{ backgroundColor: `hsl(${hue} 55% 42%)` }}
            aria-hidden
        >
            {initials(name || '?')}
        </span>
    );
}

function SellerIdentity({
    name,
    avatarUrl,
    rank,
    sharePct,
    dealCount,
    compact = false,
}: {
    name: string;
    avatarUrl?: string | null;
    rank?: number;
    sharePct?: number | null;
    dealCount?: number;
    compact?: boolean;
}) {
    const { t } = useI18n();

    return (
        <div className="flex min-w-0 items-center gap-2.5">
            {rank != null && <RankBadge rank={rank} compact={compact} />}
            <SellerAvatar name={name} avatarUrl={avatarUrl} size={compact ? 'sm' : 'md'} />
            <div className="min-w-0">
                <p className={cn('truncate font-semibold text-gray-900 dark:text-gray-100', compact ? 'text-xs' : 'text-sm')}>
                    {name}
                </p>
                {!compact && (
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                        {sharePct != null && (
                            <Badge
                                variant="flat"
                                color="success"
                                className="!px-1.5 !py-0 text-[10px] font-semibold"
                            >
                                {t('v2.finance_dashboard.share')} {sharePct.toFixed(1)}%
                            </Badge>
                        )}
                        {dealCount != null && (
                            <Badge
                                variant="flat"
                                color="info"
                                className="!px-1.5 !py-0 text-[10px] font-semibold"
                            >
                                {dealCount} {t('v2.finance_dashboard.deals')}
                            </Badge>
                        )}
                        {rank != null && rank <= 3 && (
                            <Badge
                                variant="flat"
                                color="warning"
                                className="!px-1.5 !py-0 text-[10px] font-semibold"
                            >
                                {t('v2.finance_dashboard.top_seller', { rank })}
                            </Badge>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function MetricBadge({
    value,
    alert = false,
    warn = false,
    ok = false,
}: {
    value: string;
    alert?: boolean;
    warn?: boolean;
    ok?: boolean;
}) {
    return (
        <span
            className={cn(
                'inline-flex min-w-[3.25rem] items-center justify-center rounded-full px-2.5 py-1 text-xs font-bold tabular-nums ring-1',
                alert
                    ? 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-900/40 dark:text-rose-300 dark:ring-rose-800/60'
                    : warn
                      ? 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:ring-amber-800/60'
                      : ok
                        ? 'bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:ring-emerald-800/60'
                        : 'bg-gray-100 text-gray-700 ring-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:ring-gray-600',
            )}
        >
            {value}
        </span>
    );
}

function KpiCard({
    label,
    value,
    icon: Icon,
    trend,
    trendLabel,
    hint,
    title,
    alert,
    warn,
    compact = false,
}: {
    label: string;
    value: string | number;
    icon: React.ComponentType<{ className?: string }>;
    trend?: 'up' | 'down' | 'neutral';
    trendLabel?: string;
    hint?: string;
    title?: string;
    alert?: boolean;
    warn?: boolean;
    compact?: boolean;
}) {
    return (
        <div
            title={title}
            className={cn(
                'flex flex-1 items-start gap-3 rounded-2xl border',
                compact ? 'min-w-[9rem] p-3.5' : 'min-w-[11rem] gap-4 p-5',
                alert
                    ? 'border-rose-200 bg-rose-50 dark:border-rose-800/60 dark:bg-rose-950/30'
                    : warn
                      ? 'border-amber-200 bg-amber-50 dark:border-amber-800/60 dark:bg-amber-950/30'
                      : 'border-gray-200 bg-white dark:border-gray-700/60 dark:bg-gray-800/80',
            )}
        >
            <span
                className={cn(
                    'flex shrink-0 items-center justify-center rounded-xl',
                    compact ? 'size-9' : 'size-12',
                    alert
                        ? 'bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-400'
                        : warn
                          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
                )}
            >
                <Icon className={compact ? 'size-5' : 'size-6'} />
            </span>
            <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
                <p
                    className={cn(
                        'mt-1 font-bold tabular-nums',
                        compact ? 'text-xl' : 'text-3xl',
                        alert
                            ? 'text-rose-600 dark:text-rose-400'
                            : warn
                              ? 'text-amber-800 dark:text-amber-200'
                              : 'text-gray-900 dark:text-gray-100',
                    )}
                >
                    {value}
                </p>
                {hint && <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">{hint}</p>}
                {trendLabel && (
                    <p
                        className={cn(
                            'mt-1 flex items-center gap-0.5 text-xs font-medium',
                            trend === 'up'
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : trend === 'down'
                                  ? 'text-rose-600 dark:text-rose-400'
                                  : 'text-gray-500',
                        )}
                    >
                        {trend === 'up' && <PiArrowUpRightBold className="size-3.5" />}
                        {trend === 'down' && <PiArrowDownRightBold className="size-3.5" />}
                        {trendLabel}
                    </p>
                )}
            </div>
        </div>
    );
}

type FinanceTab = 'comercial' | 'mix' | 'governance' | 'audit';

function DashCard({
    title,
    subtitle,
    note,
    action,
    children,
    className,
}: {
    title: string;
    subtitle?: string;
    note?: string;
    action?: React.ReactNode;
    children: React.ReactNode;
    className?: string;
}) {
    return (
        <section
            className={cn(
                'flex flex-col rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700/60 dark:bg-gray-800/80',
                className,
            )}
        >
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4 dark:border-gray-700/40">
                <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
                    {subtitle && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>}
                </div>
                {action}
            </div>
            <div className="flex min-h-0 flex-1 flex-col px-5 py-4">{children}</div>
            {note && (
                <p className="border-t border-gray-100 px-5 py-3 text-[11px] leading-relaxed text-gray-500 dark:border-gray-700/40 dark:text-gray-400">
                    {note}
                </p>
            )}
        </section>
    );
}

const GAP_LABEL_KEYS: Record<string, string> = {
    dre: 'v2.finance_dashboard.gap_dre',
    caixa: 'v2.finance_dashboard.gap_caixa',
    inadimplencia: 'v2.finance_dashboard.gap_inadimplencia',
    ebitda_cml: 'v2.finance_dashboard.gap_ebitda_cml',
    protheus_import: 'v2.finance_dashboard.gap_protheus_import',
};

function Bar({
    label,
    value,
    max,
    sub,
    color = 'bg-blue-500',
}: {
    label: string;
    value: number;
    max: number;
    sub?: string;
    color?: string;
}) {
    return (
        <li className="space-y-1">
            <div className="flex items-center justify-between text-xs">
                <span className="max-w-[60%] truncate font-medium text-gray-700 dark:text-gray-200">{label}</span>
                <span className="tabular-nums text-gray-500 dark:text-gray-400">
                    {sub ?? brlCompact(value)}
                </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                <div className={cn('h-full rounded-full', color)} style={{ width: `${Math.min(100, (value / Math.max(max, 1)) * 100)}%` }} />
            </div>
        </li>
    );
}

function MonthBars({ labels, values }: { labels: string[]; values: number[] }) {
    const max = Math.max(1, ...values);
    const monthLabel = (ym: string) => {
        const m = Number(ym.split('-')[1] ?? 0);
        return MONTH_SHORT[m - 1] ?? ym;
    };

    return (
        <div className="flex h-full min-h-[16rem] items-stretch gap-1.5 sm:gap-2">
            {labels.map((label, i) => {
                const v = values[i] ?? 0;
                const heightPct = v > 0 ? Math.max(6, (v / max) * 100) : 1.5;
                return (
                    <div key={label} className="flex min-h-0 min-w-0 flex-1 flex-col items-center">
                        <span className="mb-1 h-4 shrink-0 text-[10px] tabular-nums text-gray-500 dark:text-gray-400">
                            {v > 0 ? brlCompact(v) : ''}
                        </span>
                        <div className="flex w-full min-h-0 flex-1 items-end justify-center">
                            <div
                                className="w-full max-w-[2.75rem] rounded-t-md bg-emerald-500 dark:bg-emerald-400"
                                style={{ height: `${heightPct}%` }}
                                title={`${label}: ${brl(v)}`}
                            />
                        </div>
                        <span className="mt-1 shrink-0 text-[11px] font-medium text-gray-600 dark:text-gray-300">
                            {monthLabel(label)}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

function SeasonalityBars({
    labels,
    current,
    previous,
    currentYear,
    previousYear,
}: {
    labels: string[];
    current: number[];
    previous: number[];
    currentYear: number;
    previousYear: number;
}) {
    const max = Math.max(1, ...current, ...previous);

    return (
        <div className="flex h-full min-h-[16rem] flex-col gap-3">
            <div className="flex shrink-0 flex-wrap items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                <span className="inline-flex items-center gap-1.5">
                    <span className="size-2.5 rounded-sm bg-emerald-500" /> {currentYear}
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span className="size-2.5 rounded-sm bg-slate-300 dark:bg-slate-500" /> {previousYear}
                </span>
            </div>
            <div className="flex min-h-0 flex-1 items-stretch gap-1 sm:gap-1.5">
                {labels.map((label, i) => {
                    const cur = current[i] ?? 0;
                    const prev = previous[i] ?? 0;
                    const curPct = cur > 0 ? Math.max(5, (cur / max) * 100) : 1.5;
                    const prevPct = prev > 0 ? Math.max(5, (prev / max) * 100) : 1.5;
                    return (
                        <div key={label} className="flex min-h-0 min-w-0 flex-1 flex-col items-center">
                            <div className="flex w-full min-h-0 flex-1 items-end justify-center gap-0.5">
                                <div
                                    className="w-[45%] max-w-[1.25rem] rounded-t-sm bg-slate-300 dark:bg-slate-500"
                                    style={{ height: `${prevPct}%` }}
                                    title={`${previousYear} ${label}: ${brl(prev)}`}
                                />
                                <div
                                    className="w-[45%] max-w-[1.25rem] rounded-t-sm bg-emerald-500 dark:bg-emerald-400"
                                    style={{ height: `${curPct}%` }}
                                    title={`${currentYear} ${label}: ${brl(cur)}`}
                                />
                            </div>
                            <span className="mt-1 shrink-0 text-[10px] font-medium text-gray-600 dark:text-gray-300">
                                {label}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function StatPill({ label, value, hint }: { label: string; value: string; hint?: string }) {
    return (
        <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 dark:border-gray-700/50 dark:bg-gray-900/40">
            <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900 dark:text-gray-100">{value}</p>
            {hint && <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">{hint}</p>}
        </div>
    );
}

const MONTH_SHORT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function EntryMonthPanel({
    labels,
    negotiated,
    entryAmount,
    entryPct,
}: {
    labels: string[];
    negotiated: number[];
    entryAmount: number[];
    entryPct: Array<number | null>;
}) {
    const { t } = useI18n();
    const floorDefault = 30;
    const floorAlert = 20;
    const barScale = 50; // % visual max so 30% floor sits mid-bar

    const rows = labels
        .map((label, i) => ({
            label,
            month: Number(label.split('-')[1] ?? i + 1),
            negotiated: negotiated[i] ?? 0,
            entry: entryAmount[i] ?? 0,
            pct: entryPct[i],
        }))
        .filter((r) => r.negotiated > 0);

    if (rows.length === 0) {
        return <p className="py-8 text-center text-sm text-gray-400">{t('v2.finance_dashboard.empty')}</p>;
    }

    return (
        <div className="space-y-4">
            <p className="text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                {t('v2.finance_dashboard.entry_month_help')}
            </p>
            <div className="flex flex-wrap gap-2 text-[11px]">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                    <span className="size-1.5 rounded-full bg-emerald-500" />
                    {t('v2.finance_dashboard.entry_status_ok', { pct: floorDefault })}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                    <span className="size-1.5 rounded-full bg-amber-500" />
                    {t('v2.finance_dashboard.entry_status_warn', { pct: floorAlert })}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-2 py-0.5 font-medium text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
                    <span className="size-1.5 rounded-full bg-rose-500" />
                    {t('v2.finance_dashboard.entry_status_alert', { pct: floorAlert })}
                </span>
            </div>
            <ul className="space-y-3.5">
                {rows.map((row) => {
                    const p = row.pct ?? 0;
                    const status =
                        p < floorAlert ? 'alert' : p < floorDefault ? 'warn' : 'ok';
                    const barColor =
                        status === 'alert'
                            ? 'bg-rose-500'
                            : status === 'warn'
                              ? 'bg-amber-500'
                              : 'bg-emerald-500';
                    const badgeTone =
                        status === 'alert'
                            ? 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-900/40 dark:text-rose-300 dark:ring-rose-800/60'
                            : status === 'warn'
                              ? 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:ring-amber-800/60'
                              : 'bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:ring-emerald-800/60';

                    return (
                        <li key={row.label} className="space-y-1.5">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                                        {MONTH_SHORT[row.month - 1] ?? row.label}
                                    </p>
                                    <p className="mt-0.5 text-[11px] tabular-nums text-gray-500 dark:text-gray-400">
                                        {t('v2.finance_dashboard.entry_month_breakdown', {
                                            entry: brlCompact(row.entry),
                                            negotiated: brlCompact(row.negotiated),
                                        })}
                                    </p>
                                </div>
                                <span
                                    className={cn(
                                        'inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-bold tabular-nums ring-1',
                                        badgeTone,
                                    )}
                                >
                                    {pct(row.pct)}
                                </span>
                            </div>
                            <div className="relative h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                                <div
                                    className={cn('h-full rounded-full transition-all', barColor)}
                                    style={{ width: `${Math.min(100, (p / barScale) * 100)}%` }}
                                />
                                <span
                                    className="absolute top-0 h-full w-px bg-gray-400/80 dark:bg-gray-300/50"
                                    style={{ left: `${(floorDefault / barScale) * 100}%` }}
                                    title={t('v2.finance_dashboard.entry_floor_mark', { pct: floorDefault })}
                                />
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

export default function FinanceDashboardPage() {
    const { t } = useI18n();
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState(currentYear);
    const [tab, setTab] = useState<FinanceTab>('comercial');

    const { data, isLoading } = useQuery<FinanceData>({
        queryKey: ['dashboard', 'finance', year],
        queryFn: async () => (await api.get('/dashboard/finance', { params: { year } })).data,
    });

    if (isLoading) {
        return <LoadingState variant="dashboard" />;
    }

    const kpis = data?.kpis;
    const monthly = data?.monthly;
    const seasonality = data?.seasonality;
    const sellers = data?.sellers ?? [];
    const gov = data?.governance;
    const entryMonth = data?.entry_by_month;
    const categories = data?.by_category ?? [];
    const categoryContract = data?.by_category_contract ?? [];
    const products = data?.top_products ?? [];
    const ufs = data?.by_uf ?? [];
    const contracts = data?.by_contract_type ?? [];
    const pmr = data?.pmr;
    const crossSell = data?.cross_sell;
    const audit = data?.audit;
    const revenueSources = data?.revenue_sources;
    const buckets = crossSell?.buckets;

    const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

    const momTrend = (kpis?.mom_pct ?? 0) >= 0 ? 'up' : 'down';
    const yoyTrend = (kpis?.yoy_pct ?? 0) >= 0 ? 'up' : 'down';
    const revenueDelta = revenueSources?.delta ?? 0;
    const hasRevenueDelta = Math.abs(revenueDelta) > 0.009;
    const mbCoverage = kpis?.mb_coverage_pct ?? null;
    const mbLowCoverage = mbCoverage != null && mbCoverage < 50;

    const tabs: Array<{ id: FinanceTab; label: string; badge?: number }> = [
        { id: 'comercial', label: t('v2.finance_dashboard.tab_comercial') },
        { id: 'mix', label: t('v2.finance_dashboard.tab_mix') },
        {
            id: 'governance',
            label: t('v2.finance_dashboard.tab_governance'),
            badge: (gov?.high_discount_count ?? 0) + (gov?.low_entry_count ?? 0),
        },
        {
            id: 'audit',
            label: t('v2.finance_dashboard.tab_audit'),
            badge: audit?.open_gaps?.length,
        },
    ];

    const crossSellRows = buckets
        ? [
              {
                  key: 'single',
                  label: t('v2.finance_dashboard.cross_sell_single'),
                  ...buckets.single,
              },
              {
                  key: 'multi',
                  label: t('v2.finance_dashboard.cross_sell_multi_bucket'),
                  ...buckets.multi,
              },
              {
                  key: 'full',
                  label: t('v2.finance_dashboard.cross_sell_full', {
                      n: crossSell?.category_count_benchmark ?? 7,
                  }),
                  ...buckets.full,
              },
          ]
        : [];

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {t('v2.finance_dashboard.title')}
                    </h1>
                    <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                        {t('v2.finance_dashboard.subtitle')}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        {t('v2.finance_dashboard.year')}
                    </label>
                    <select
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                        className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                    >
                        {yearOptions.map((y) => (
                            <option key={y} value={y}>
                                {y}
                            </option>
                        ))}
                    </select>
                    <Link
                        to="/ploomes-deals"
                        className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                        {t('v2.finance_dashboard.view_deals')} <PiArrowRightBold className="size-4" />
                    </Link>
                </div>
            </div>

            <div
                className={cn(
                    'flex flex-col gap-2 rounded-2xl border px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between',
                    hasRevenueDelta
                        ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-200'
                        : 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700/60 dark:bg-gray-800/50 dark:text-gray-300',
                )}
            >
                <p>{t('v2.finance_dashboard.banner')}</p>
                <span
                    className={cn(
                        'inline-flex shrink-0 items-center rounded-full px-3 py-1 text-xs font-semibold tabular-nums ring-1',
                        hasRevenueDelta
                            ? 'bg-amber-100 text-amber-900 ring-amber-300 dark:bg-amber-900/50 dark:text-amber-100 dark:ring-amber-700'
                            : 'bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:ring-emerald-800',
                    )}
                >
                    {hasRevenueDelta
                        ? t('v2.finance_dashboard.revenue_delta_chip', { amount: brl(revenueDelta) })
                        : t('v2.finance_dashboard.revenue_delta_ok')}
                </span>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <KpiCard
                    label={t('v2.finance_dashboard.ytd_amount')}
                    value={brlCompact(kpis?.ytd_amount ?? 0)}
                    title={brl(kpis?.ytd_amount ?? 0)}
                    icon={PiCurrencyDollarBold}
                    trend={yoyTrend}
                    trendLabel={`${t('v2.finance_dashboard.yoy')} ${pct(kpis?.yoy_pct)}`}
                />
                <KpiCard
                    label={t('v2.finance_dashboard.mtd_amount')}
                    value={brlCompact(kpis?.mtd_amount ?? 0)}
                    title={brl(kpis?.mtd_amount ?? 0)}
                    icon={PiTrendUpBold}
                    trend={momTrend}
                    trendLabel={`${t('v2.finance_dashboard.mom')} ${pct(kpis?.mom_pct)}`}
                />
                <KpiCard
                    label={t('v2.finance_dashboard.ticket_avg')}
                    value={kpis?.ticket_avg != null ? brlCompact(kpis.ticket_avg) : '—'}
                    title={kpis?.ticket_avg != null ? brl(kpis.ticket_avg) : undefined}
                    icon={PiReceiptBold}
                />
                <KpiCard
                    label={t('v2.finance_dashboard.mb_pct')}
                    value={kpis?.mb_pct != null ? pct(kpis.mb_pct) : '—'}
                    title={kpis?.mb_pct != null ? pct(kpis.mb_pct) : undefined}
                    icon={PiPercentBold}
                    warn={mbLowCoverage}
                    hint={
                        mbCoverage != null
                            ? t('v2.finance_dashboard.mb_coverage_hint', { pct: pct(mbCoverage) })
                            : undefined
                    }
                />
            </div>

            <div>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    {t('v2.finance_dashboard.secondary_kpis')}
                </p>
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                    <KpiCard
                        compact
                        label={t('v2.finance_dashboard.ytd_count')}
                        value={kpis?.ytd_count ?? 0}
                        icon={PiHandshakeBold}
                    />
                    <KpiCard
                        compact
                        label={t('v2.finance_dashboard.distinct_clients')}
                        value={kpis?.distinct_clients ?? 0}
                        icon={PiBuildingsBold}
                    />
                    <KpiCard
                        compact
                        label={t('v2.finance_dashboard.mb_coverage')}
                        value={mbCoverage != null ? pct(mbCoverage) : '—'}
                        icon={PiStackBold}
                        warn={mbLowCoverage}
                    />
                    <KpiCard
                        compact
                        label={t('v2.finance_dashboard.high_discount', { pct: gov?.discount_ceiling_pct ?? 35 })}
                        value={gov?.high_discount_count ?? 0}
                        icon={PiWarningBold}
                        alert={(gov?.high_discount_count ?? 0) > 0}
                    />
                </div>
            </div>

            <div
                role="tablist"
                aria-label={t('v2.finance_dashboard.title')}
                className="flex flex-wrap gap-1 rounded-2xl border border-gray-200 bg-gray-50 p-1 dark:border-gray-700 dark:bg-gray-800/60"
            >
                {tabs.map((item) => (
                    <button
                        key={item.id}
                        type="button"
                        role="tab"
                        aria-selected={tab === item.id}
                        onClick={() => setTab(item.id)}
                        className={cn(
                            'inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-medium transition',
                            tab === item.id
                                ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-100'
                                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200',
                        )}
                    >
                        {item.label}
                        {item.badge != null && item.badge > 0 && (
                            <span
                                className={cn(
                                    'rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums',
                                    item.id === 'governance' || item.id === 'audit'
                                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200'
                                        : 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200',
                                )}
                            >
                                {item.badge}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {tab === 'comercial' && (
                <div className="space-y-5">
                    <div className="grid gap-5 lg:grid-cols-3 lg:items-stretch">
                        <DashCard
                            title={t('v2.finance_dashboard.monthly_title')}
                            subtitle={t('v2.finance_dashboard.monthly_subtitle')}
                            note={t('v2.finance_dashboard.monthly_note')}
                            className="h-full lg:col-span-2"
                        >
                            {monthly ? (
                                <MonthBars labels={monthly.labels} values={monthly.amounts} />
                            ) : (
                                <p className="py-8 text-center text-sm text-gray-400">
                                    {t('v2.finance_dashboard.empty')}
                                </p>
                            )}
                        </DashCard>

                        <DashCard
                            title={t('v2.finance_dashboard.entry_month_title')}
                            subtitle={t('v2.finance_dashboard.entry_month_subtitle')}
                        >
                            {entryMonth ? (
                                <EntryMonthPanel
                                    labels={entryMonth.labels}
                                    negotiated={entryMonth.negotiated}
                                    entryAmount={entryMonth.entry_amount}
                                    entryPct={entryMonth.entry_pct}
                                />
                            ) : (
                                <p className="py-8 text-center text-sm text-gray-400">
                                    {t('v2.finance_dashboard.empty')}
                                </p>
                            )}
                        </DashCard>
                    </div>

                    <div className="grid gap-5 lg:grid-cols-3 lg:items-stretch">
                        <DashCard
                            title={t('v2.finance_dashboard.seasonality_title')}
                            subtitle={t('v2.finance_dashboard.seasonality_subtitle')}
                            className="h-full lg:col-span-2"
                        >
                            {seasonality ? (
                                <SeasonalityBars
                                    labels={seasonality.labels}
                                    current={seasonality.current}
                                    previous={seasonality.previous}
                                    currentYear={seasonality.current_year}
                                    previousYear={seasonality.previous_year}
                                />
                            ) : (
                                <p className="py-8 text-center text-sm text-gray-400">
                                    {t('v2.finance_dashboard.empty')}
                                </p>
                            )}
                        </DashCard>

                        <DashCard
                            title={t('v2.finance_dashboard.pmr_title')}
                            subtitle={t('v2.finance_dashboard.pmr_subtitle')}
                            note={t('v2.finance_dashboard.pmr_note')}
                            action={<PiClockBold className="size-4 text-gray-400" />}
                        >
                            <div className="grid grid-cols-2 gap-3">
                                <StatPill
                                    label={t('v2.finance_dashboard.pmr_avg')}
                                    value={pmr?.avg_months != null ? `${pmr.avg_months}` : '—'}
                                    hint={t('v2.finance_dashboard.pmr_months_unit')}
                                />
                                <StatPill
                                    label={t('v2.finance_dashboard.pmr_sample')}
                                    value={String(pmr?.sample_size ?? 0)}
                                />
                                <StatPill
                                    label={t('v2.finance_dashboard.pmr_sale')}
                                    value={pmr?.sale_months != null ? `${pmr.sale_months}` : '—'}
                                />
                                <StatPill
                                    label={t('v2.finance_dashboard.pmr_rental')}
                                    value={pmr?.rental_months != null ? `${pmr.rental_months}` : '—'}
                                />
                            </div>
                        </DashCard>
                    </div>

                    <DashCard
                        title={t('v2.finance_dashboard.sellers_title')}
                        subtitle={t('v2.finance_dashboard.sellers_subtitle')}
                        note={t('v2.finance_dashboard.sellers_note')}
                        action={
                            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                <PiUsersBold className="size-3.5" /> {sellers.length}
                            </span>
                        }
                    >
                {sellers.length === 0 ? (
                    <p className="py-8 text-center text-sm text-gray-400">{t('v2.finance_dashboard.empty')}</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 dark:border-gray-700/40">
                                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {t('v2.finance_dashboard.seller')}
                                    </th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {t('v2.finance_dashboard.amount')}
                                    </th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {t('v2.finance_dashboard.deals')}
                                    </th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {t('v2.finance_dashboard.clients')}
                                    </th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {t('v2.finance_dashboard.ticket')}
                                    </th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {t('v2.finance_dashboard.discount_avg')}
                                    </th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {t('v2.finance_dashboard.entry_avg')}
                                    </th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {t('v2.finance_dashboard.pmr_short')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sellers.slice(0, 15).map((s) => (
                                    <tr
                                        key={s.owner_name}
                                        className="border-b border-gray-50 last:border-b-0 hover:bg-gray-50/60 dark:border-gray-700/30 dark:hover:bg-gray-700/20"
                                    >
                                        <td className="px-3 py-2.5">
                                            <SellerIdentity
                                                name={s.owner_name}
                                                avatarUrl={s.avatar_url}
                                                rank={s.rank}
                                                sharePct={s.share_pct}
                                                dealCount={s.deal_count}
                                            />
                                        </td>
                                        <td className="px-3 py-2.5 text-right">
                                            <MetricBadge value={brlCompact(s.amount)} ok />
                                        </td>
                                        <td className="px-3 py-2.5 text-right">
                                            <MetricBadge value={String(s.deal_count)} />
                                        </td>
                                        <td className="px-3 py-2.5 text-right">
                                            <MetricBadge value={String(s.client_count ?? '—')} />
                                        </td>
                                        <td className="px-3 py-2.5 text-right">
                                            <MetricBadge
                                                value={s.ticket_avg != null ? brlCompact(s.ticket_avg) : '—'}
                                                ok={s.ticket_avg != null}
                                            />
                                        </td>
                                        <td className="px-3 py-2.5 text-right">
                                            <MetricBadge
                                                value={pct(s.discount_avg_simple)}
                                                alert={(s.discount_avg_simple ?? 0) > 35}
                                                warn={(s.discount_avg_simple ?? 0) > 30}
                                                ok={
                                                    s.discount_avg_simple != null &&
                                                    s.discount_avg_simple <= 30
                                                }
                                            />
                                        </td>
                                        <td className="px-3 py-2.5 text-right">
                                            <MetricBadge
                                                value={pct(s.entry_avg_pct)}
                                                alert={s.entry_avg_pct != null && s.entry_avg_pct < 20}
                                                warn={s.entry_avg_pct != null && s.entry_avg_pct < 30}
                                                ok={s.entry_avg_pct != null && s.entry_avg_pct >= 30}
                                            />
                                        </td>
                                        <td className="px-3 py-2.5 text-right">
                                            <MetricBadge
                                                value={s.pmr_months != null ? String(s.pmr_months) : '—'}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                    </DashCard>
                </div>
            )}

            {tab === 'governance' && (
            <div className="grid gap-5 lg:grid-cols-2">
                <DashCard
                    title={t('v2.finance_dashboard.governance_title')}
                    subtitle={t('v2.finance_dashboard.high_discount', { pct: gov?.discount_ceiling_pct ?? 35 })}
                    action={
                        <span className="rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-semibold text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
                            {gov?.high_discount_count ?? 0}/{gov?.total_deals ?? 0}
                        </span>
                    }
                >
                    {(gov?.high_discount_deals?.length ?? 0) === 0 ? (
                        <p className="py-8 text-center text-sm text-gray-400">{t('v2.finance_dashboard.empty')}</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-gray-100 dark:border-gray-700/40">
                                        <th className="px-2 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                                            {t('v2.finance_dashboard.client')}
                                        </th>
                                        <th className="px-2 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                                            {t('v2.finance_dashboard.seller')}
                                        </th>
                                        <th className="px-2 py-2 text-right text-xs font-semibold uppercase text-gray-500">
                                            {t('v2.finance_dashboard.amount')}
                                        </th>
                                        <th className="px-2 py-2 text-right text-xs font-semibold uppercase text-gray-500">
                                            {t('v2.finance_dashboard.discount')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(gov?.high_discount_deals ?? []).slice(0, 12).map((d) => (
                                        <tr key={d.id} className="border-b border-gray-50 last:border-0 dark:border-gray-700/30">
                                            <td className="max-w-[10rem] truncate px-2 py-2 text-gray-800 dark:text-gray-200">
                                                {d.contact_name || d.title || `#${d.id}`}
                                            </td>
                                            <td className="px-2 py-2">
                                                <SellerIdentity
                                                    name={d.owner_name || '—'}
                                                    avatarUrl={d.owner_avatar_url}
                                                    compact
                                                />
                                            </td>
                                            <td className="px-2 py-2 text-right tabular-nums">{brlCompact(d.amount)}</td>
                                            <td className="px-2 py-2 text-right">
                                                <MetricBadge value={pct(d.discount_pct)} alert />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </DashCard>

                <DashCard
                    title={t('v2.finance_dashboard.low_entry')}
                    subtitle={t('v2.finance_dashboard.governance_subtitle')}
                    action={
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                            <PiPercentBold className="size-3.5" />
                            {gov?.low_entry_count ?? 0}
                        </span>
                    }
                >
                    {(gov?.low_entry_deals?.length ?? 0) === 0 ? (
                        <p className="py-8 text-center text-sm text-gray-400">{t('v2.finance_dashboard.empty')}</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-gray-100 dark:border-gray-700/40">
                                        <th className="px-2 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                                            {t('v2.finance_dashboard.client')}
                                        </th>
                                        <th className="px-2 py-2 text-right text-xs font-semibold uppercase text-gray-500">
                                            {t('v2.finance_dashboard.entry')}
                                        </th>
                                        <th className="px-2 py-2 text-right text-xs font-semibold uppercase text-gray-500">
                                            {t('v2.finance_dashboard.floor')}
                                        </th>
                                        <th className="px-2 py-2 text-right text-xs font-semibold uppercase text-gray-500">
                                            {t('v2.finance_dashboard.amount')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(gov?.low_entry_deals ?? []).slice(0, 12).map((d) => (
                                        <tr key={d.id} className="border-b border-gray-50 last:border-0 dark:border-gray-700/30">
                                            <td className="px-2 py-2">
                                                <div className="flex min-w-0 flex-col gap-1">
                                                    <span className="truncate text-gray-800 dark:text-gray-200">
                                                        {d.contact_name || d.title || `#${d.id}`}
                                                    </span>
                                                    {d.owner_name && (
                                                        <SellerIdentity
                                                            name={d.owner_name}
                                                            avatarUrl={d.owner_avatar_url}
                                                            compact
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-2 py-2 text-right">
                                                <MetricBadge value={pct(d.entry_pct)} warn />
                                            </td>
                                            <td className="px-2 py-2 text-right tabular-nums text-gray-500">{pct(d.entry_floor_pct)}</td>
                                            <td className="px-2 py-2 text-right tabular-nums">{brlCompact(d.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </DashCard>
            </div>
            )}

            {tab === 'mix' && (
            <div className="space-y-5">
            <DashCard
                title={t('v2.finance_dashboard.cross_sell_title')}
                subtitle={t('v2.finance_dashboard.cross_sell_subtitle')}
                note={t('v2.finance_dashboard.cross_sell_note')}
                action={<PiStackBold className="size-4 text-gray-400" />}
            >
                {crossSellRows.length === 0 ? (
                    <p className="py-4 text-center text-sm text-gray-400">{t('v2.finance_dashboard.empty')}</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 dark:border-gray-700/40">
                                    <th className="px-2 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                                        {t('v2.finance_dashboard.cross_sell_bucket')}
                                    </th>
                                    <th className="px-2 py-2 text-right text-xs font-semibold uppercase text-gray-500">
                                        {t('v2.finance_dashboard.clients')}
                                    </th>
                                    <th className="px-2 py-2 text-right text-xs font-semibold uppercase text-gray-500">
                                        {t('v2.finance_dashboard.amount')}
                                    </th>
                                    <th className="px-2 py-2 text-right text-xs font-semibold uppercase text-gray-500">
                                        {t('v2.finance_dashboard.cross_sell_amount_pct')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {crossSellRows.map((row) => (
                                    <tr
                                        key={row.key}
                                        className="border-b border-gray-50 last:border-0 dark:border-gray-700/30"
                                    >
                                        <td className="px-2 py-2 text-gray-800 dark:text-gray-200">{row.label}</td>
                                        <td className="px-2 py-2 text-right tabular-nums">{row.clients}</td>
                                        <td className="px-2 py-2 text-right tabular-nums">
                                            {brlCompact(row.amount)}
                                        </td>
                                        <td className="px-2 py-2 text-right tabular-nums">
                                            {pct(row.amount_pct)}
                                        </td>
                                    </tr>
                                ))}
                                <tr className="border-t border-gray-200 font-semibold dark:border-gray-600">
                                    <td className="px-2 py-2">{t('v2.finance_dashboard.cross_sell_total')}</td>
                                    <td className="px-2 py-2 text-right tabular-nums">
                                        {crossSell?.total_clients ?? 0}
                                    </td>
                                    <td className="px-2 py-2 text-right tabular-nums">
                                        {brlCompact(crossSell?.total_amount ?? 0)}
                                    </td>
                                    <td className="px-2 py-2 text-right tabular-nums">100%</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </DashCard>

            <DashCard
                title={t('v2.finance_dashboard.category_contract_title')}
                subtitle={t('v2.finance_dashboard.category_contract_subtitle')}
            >
                {categoryContract.length === 0 ? (
                    <p className="py-8 text-center text-sm text-gray-400">{t('v2.finance_dashboard.empty')}</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 dark:border-gray-700/40">
                                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {t('v2.finance_dashboard.category')}
                                    </th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {t('v2.finance_dashboard.qty_sale')}
                                    </th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {t('v2.finance_dashboard.qty_rental')}
                                    </th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {t('v2.finance_dashboard.amount_sale')}
                                    </th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {t('v2.finance_dashboard.amount_rental')}
                                    </th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {t('v2.finance_dashboard.amount')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {categoryContract.slice(0, 12).map((row) => (
                                    <tr
                                        key={row.category}
                                        className="border-b border-gray-50 last:border-0 dark:border-gray-700/30"
                                    >
                                        <td className="px-3 py-2.5">
                                            <Badge
                                                variant="flat"
                                                color="secondary"
                                                className="!px-2 !py-0.5 text-[11px] font-semibold"
                                            >
                                                {row.category}
                                            </Badge>
                                        </td>
                                        <td className="px-3 py-2.5 text-right">
                                            <MetricBadge value={String(row.qty_sale)} ok={row.qty_sale > 0} />
                                        </td>
                                        <td className="px-3 py-2.5 text-right">
                                            <MetricBadge
                                                value={String(row.qty_rental)}
                                                warn={row.qty_rental > 0}
                                            />
                                        </td>
                                        <td className="px-3 py-2.5 text-right">
                                            <MetricBadge
                                                value={brlCompact(row.amount_sale)}
                                                ok={row.amount_sale > 0}
                                            />
                                        </td>
                                        <td className="px-3 py-2.5 text-right">
                                            <MetricBadge
                                                value={brlCompact(row.amount_rental)}
                                                warn={row.amount_rental > 0}
                                            />
                                        </td>
                                        <td className="px-3 py-2.5 text-right">
                                            <MetricBadge value={brlCompact(row.amount)} ok />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </DashCard>

            <div className="grid gap-5 md:grid-cols-2">
                <DashCard title={t('v2.finance_dashboard.categories_title')}>
                    {categories.length === 0 ? (
                        <p className="py-6 text-center text-sm text-gray-400">{t('v2.finance_dashboard.empty')}</p>
                    ) : (
                        <ul className="space-y-3">
                            {categories.slice(0, 8).map((c) => (
                                <Bar
                                    key={c.category}
                                    label={c.category}
                                    value={c.amount}
                                    max={categories[0]?.amount ?? 1}
                                    color="bg-violet-500"
                                />
                            ))}
                        </ul>
                    )}
                </DashCard>

                <DashCard title={t('v2.finance_dashboard.products_title')}>
                    {products.length === 0 ? (
                        <p className="py-6 text-center text-sm text-gray-400">{t('v2.finance_dashboard.empty')}</p>
                    ) : (
                        <ul className="space-y-3">
                            {products.map((p) => (
                                <Bar
                                    key={p.product_name}
                                    label={p.product_name}
                                    value={p.amount}
                                    max={products[0]?.amount ?? 1}
                                    sub={`${brlCompact(p.amount)} · ${p.quantity} un`}
                                    color="bg-indigo-500"
                                />
                            ))}
                        </ul>
                    )}
                </DashCard>

                <DashCard title={t('v2.finance_dashboard.uf_title')}>
                    {ufs.length === 0 ? (
                        <p className="py-6 text-center text-sm text-gray-400">{t('v2.finance_dashboard.empty')}</p>
                    ) : (
                        <ul className="space-y-3">
                            {ufs.slice(0, 8).map((u) => (
                                <Bar
                                    key={u.uf}
                                    label={u.uf}
                                    value={u.amount}
                                    max={ufs[0]?.amount ?? 1}
                                    color="bg-teal-500"
                                />
                            ))}
                        </ul>
                    )}
                </DashCard>

                <DashCard title={t('v2.finance_dashboard.contract_title')}>
                    {contracts.length === 0 ? (
                        <p className="py-6 text-center text-sm text-gray-400">{t('v2.finance_dashboard.empty')}</p>
                    ) : (
                        <ul className="space-y-3">
                            {contracts.map((c) => (
                                <Bar
                                    key={c.contract_type}
                                    label={c.contract_type}
                                    value={c.amount}
                                    max={contracts[0]?.amount ?? 1}
                                    sub={`${brlCompact(c.amount)} · ${c.deal_count}`}
                                    color="bg-orange-500"
                                />
                            ))}
                        </ul>
                    )}
                </DashCard>
            </div>
            </div>
            )}

            {tab === 'audit' && (
            <DashCard
                title={t('v2.finance_dashboard.audit_title')}
                subtitle={t('v2.finance_dashboard.audit_subtitle')}
                note={t('v2.finance_dashboard.mb_note')}
            >
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <StatPill
                        label={t('v2.finance_dashboard.audit_revenue_delta')}
                        value={brl(audit?.revenue_delta_deal_vs_negotiated ?? 0)}
                    />
                    <StatPill
                        label={t('v2.finance_dashboard.audit_mb_coverage')}
                        value={pct(audit?.mb_coverage_pct)}
                    />
                    <StatPill
                        label={t('v2.finance_dashboard.audit_pmr_sample')}
                        value={String(audit?.pmr_sample_size ?? 0)}
                    />
                    <StatPill
                        label={t('v2.finance_dashboard.audit_cross_sell_unassigned')}
                        value={brl(audit?.cross_sell_unassigned_amount ?? 0)}
                    />
                </div>
                <div className="mt-5">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {t('v2.finance_dashboard.audit_gaps_title')}
                    </p>
                    <ul className="space-y-1.5">
                        {(audit?.open_gaps ?? []).map((gap) => (
                            <li
                                key={gap}
                                className="rounded-lg border border-gray-100 px-3 py-2 text-sm text-gray-700 dark:border-gray-700/40 dark:text-gray-300"
                            >
                                {t(GAP_LABEL_KEYS[gap] ?? gap)}
                            </li>
                        ))}
                    </ul>
                </div>
            </DashCard>
            )}
        </div>
    );
}
